import React, { useEffect, useState } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import { Print } from '@mui/icons-material';
import { useRouter } from '../contexts/RouterContext';
import { useAuth } from '../contexts/AuthContext';
import { patientNotesService, PatientNote } from '../services/patientNotesService';
import { patientsService, PatientDetail } from '../services/patientsService';
import { printTemplateService, PrintTemplate } from '../services/printTemplateService';
import { PrintTemplateBar, usePrintTemplateChoice } from './print/PrintTemplateChoice';
import { colors } from '../theme/designSystem';

/**
 * Impressão das anotações selecionadas (RN-534e): identificação do paciente no corpo e
 * cabeçalho/rodapé vindos do TEMPLATE PADRÃO da entidade (o mesmo de Gerenciar entidade →
 * Templates: logo à esquerda, cabeçalho HTML à direita, rodapé centralizado — layout do
 * TemplatePreview). O rodapé sai fixo no pé de cada página impressa.
 */
const NotesPrint: React.FC = () => {
  const { getParam } = useRouter();
  const { user } = useAuth();
  const patientId = getParam('patientId') || '';
  const idsParam = getParam('ids') || '';

  const [patient, setPatient] = useState<PatientDetail | null>(null);
  const [notes, setNotes] = useState<PatientNote[]>([]);
  const [templates, setTemplates] = useState<PrintTemplate[]>([]);
  const templateChoice = usePrintTemplateChoice(templates, 'notes');
  const template = templateChoice.template;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    document.title = 'Clinic4Us - Impressão de Anotações';
    const load = async () => {
      try {
        const wanted = new Set(idsParam.split(',').filter(Boolean));
        const [patientData, notesResult, templates] = await Promise.all([
          patientsService.getById(patientId),
          patientNotesService.getByPatient(patientId, { pageNumber: 1, pageSize: 100 }),
          // Sem template (ou sem permissão de lê-lo), a impressão sai sem cabeçalho/rodapé — não falha
          user?.entityId ? printTemplateService.getByEntity(user.entityId).catch(() => []) : Promise.resolve([]),
        ]);
        setPatient(patientData);
        // Impresso em ordem cronológica (a lista da tela é decrescente; registro se lê do começo)
        setNotes(notesResult.items
          .filter(n => wanted.has(n.id))
          .sort((a, b) => a.createdAt.localeCompare(b.createdAt)));
        setTemplates(templates);
      } catch {
        setError('Não foi possível carregar os dados para impressão.');
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, idsParam]);

  const formatIsoDate = (iso: string): string => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
  const timeOf = (iso: string): string => iso.slice(11, 16);
  const birthDate = patient?.patientBirthDate
    ? formatIsoDate(patient.patientBirthDate.slice(0, 10))
    : '—';

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress sx={{ color: colors.primary }} />
      </Box>
    );
  }

  if (error || !patient) {
    return (
      <Typography sx={{ color: colors.error, textAlign: 'center', py: 8 }}>
        {error || 'Paciente não encontrado.'}
      </Typography>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '2rem', '@media print': { padding: 0, backgroundColor: '#fff' } }}>
      {/* Botão de imprimir — fora da impressão */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, maxWidth: '210mm', mx: 'auto', '@media print': { display: 'none' } }}>
        <PrintTemplateBar choice={templateChoice} />
        <Button
          variant="contained"
          startIcon={<Print />}
          onClick={() => window.print()}
          sx={{ backgroundColor: colors.primary, textTransform: 'none', '&:hover': { backgroundColor: '#029AAB' } }}
        >
          Imprimir
        </Button>
      </Box>

      {/* Página A4 no layout do template padrão (TemplatePreview) */}
      <Box
        sx={{
          width: '210mm',
          maxWidth: '100%',
          minHeight: '297mm',
          padding: '15mm',
          paddingBottom: '30mm',
          boxSizing: 'border-box',
          background: 'white',
          margin: '0 auto',
          position: 'relative',
          boxShadow: '0 0 8px rgba(0,0,0,0.15)',
          fontSize: '12pt',
          color: '#000',
          '@media print': { boxShadow: 'none', width: 'auto', minHeight: 'auto', margin: 0 },
        }}
      >
        {/* Cabeçalho do template: logo à esquerda, HTML do cabeçalho à direita */}
        {template && (<>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10mm' }}>
          <div style={{ flexShrink: 0 }}>
            {template?.file ? (
              <img src={template.file} alt="logo" style={{ maxHeight: '30mm', maxWidth: '70mm', objectFit: 'contain' }} />
            ) : null}
          </div>
          <div
            style={{ textAlign: 'right', fontSize: '10pt', lineHeight: 1.5 }}
            dangerouslySetInnerHTML={{ __html: template?.header || '' }}
          />
        </div>

        <hr style={{ border: 'none', borderTop: '1px solid #999', margin: '6mm 0' }} />
        </>)}

        {/* Miolo no layout da impressão de Evoluções: título com a data de emissão,
            identificação do paciente e tabela Data × conteúdo */}
        <Box sx={{ textAlign: 'center', mb: '6mm' }}>
          <Typography sx={{ fontSize: '13pt', fontWeight: 700, color: '#212529' }}>
            Anotações do Paciente
          </Typography>
        </Box>

        <Box sx={{ mb: '6mm' }}>
          <Box sx={{ display: 'flex', gap: '3rem', mb: '4px' }}>
            <Typography sx={{ fontSize: '11px', color: '#212529' }}>
              Paciente: {patient.patientName}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: '#212529' }}>
              Data Nasc.: {birthDate}
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '11px', color: '#212529', mb: '4px' }}>
            Responsável: {patient.responsibleName1 || '—'}
          </Typography>
          <Typography sx={{ fontSize: '11px', color: '#212529' }}>
            Data de emissão: {new Date().toLocaleDateString('pt-BR')}
          </Typography>
        </Box>

        <Box sx={{ border: '1px solid #dee2e6', borderRadius: '4px', overflow: 'hidden' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '100px 1fr', backgroundColor: '#f1f3f5', borderBottom: '1px solid #dee2e6' }}>
            <Box sx={{ p: '8px', borderRight: '1px solid #dee2e6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#212529' }}>Data</Typography>
            </Box>
            <Box sx={{ p: '8px', display: 'flex', alignItems: 'center' }}>
              <Typography sx={{ fontSize: '11px', fontWeight: 700, color: '#212529' }}>Anotação</Typography>
            </Box>
          </Box>

          {notes.length === 0 ? (
            <Box sx={{ p: '10px' }}>
              <Typography sx={{ fontSize: '11px', color: '#555' }}>Nenhuma anotação selecionada.</Typography>
            </Box>
          ) : (
            notes.map((note, index) => (
              <Box
                key={note.id}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '100px 1fr',
                  borderBottom: index < notes.length - 1 ? '1px solid #dee2e6' : 'none',
                  breakInside: 'avoid',
                  '&:nth-of-type(even)': { backgroundColor: '#f8f9fa' },
                }}
              >
                <Box sx={{ p: '10px', borderRight: '1px solid #dee2e6', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#212529', lineHeight: 1.2 }}>
                    {formatIsoDate(note.createdAt)}
                  </Typography>
                  <Typography sx={{ fontSize: '9px', color: '#6c757d', lineHeight: 1.2 }}>
                    {timeOf(note.createdAt)}
                  </Typography>
                </Box>
                <Box sx={{ p: '10px' }}>
                  <Typography sx={{ fontSize: '10px', fontWeight: 600, color: '#212529', mb: '2px' }}>
                    {note.createdByName}
                  </Typography>
                  <Typography sx={{ fontSize: '11px', color: '#212529', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.5 }}>
                    {note.note}
                  </Typography>
                </Box>
              </Box>
            ))
          )}
        </Box>

        {/* Rodapé do template: centralizado; na impressão sai no pé de TODAS as páginas */}
        {template && (
        <Box
          className="notes-print-footer"
          sx={{
            position: 'absolute',
            left: '15mm',
            right: '15mm',
            bottom: '8mm',
            textAlign: 'center',
            fontSize: '9pt',
            '@media print': { position: 'fixed', left: 0, right: 0, bottom: '8mm' },
          }}
          dangerouslySetInnerHTML={{ __html: template?.footer || '' }}
        />
        )}
      </Box>
    </Box>
  );
};

export default NotesPrint;
